import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronDown, Menu, Phone, X } from "lucide-react";
import { COMPANY, NAV } from "@/data/company";
import { EASE } from "@/components/motion/Reveal";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const Dropdown = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <NavLink
        to={item.to}
        data-testid={`nav-link-${slug(item.label)}`}
        className={({ isActive }) => `flex items-center gap-1 px-4 py-2 font-display text-[15px] font-semibold uppercase tracking-wider transition-colors ${isActive ? "text-white" : "text-white/70 hover:text-white"}`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </NavLink>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.3, ease: EASE }}
            className="absolute left-0 top-full pt-3"
          >
            <div className="w-72 border border-white/10 bg-ink-2/95 p-2 shadow-2xl backdrop-blur-xl">
              {item.children.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  data-testid={`nav-link-${slug(c.label)}`}
                  className="group flex items-start justify-between gap-3 px-4 py-3 transition-colors hover:bg-white/5"
                >
                  <span>
                    <span className="block font-display text-base font-bold uppercase tracking-wide text-white">{c.label}</span>
                    <span className="block text-xs text-white/50">{c.desc}</span>
                  </span>
                  <ArrowRight className="mt-1 h-4 w-4 text-blue-500 -translate-x-1 opacity-0 transition-[opacity,transform] duration-300 group-hover:translate-x-0 group-hover:opacity-100" />
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const MobileMenu = ({ open, onClose }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        initial={{ clipPath: "inset(0 0 100% 0)" }}
        animate={{ clipPath: "inset(0 0 0% 0)" }}
        exit={{ clipPath: "inset(0 0 100% 0)" }}
        transition={{ duration: 0.6, ease: EASE }}
        className="fixed inset-0 z-40 flex flex-col bg-ink pt-28 pb-10 lg:hidden"
        data-testid="mobile-menu"
      >
        <div className="absolute inset-0 blueprint" />
        <nav className="container-x relative flex-1 overflow-y-auto">
          {NAV.map((n, i) => (
            <motion.div
              key={n.to}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 + i * 0.07, duration: 0.6, ease: EASE }}
              className="border-b border-white/10 py-4"
            >
              <Link to={n.to} onClick={onClose} className="display block text-4xl text-white" data-testid={`mobile-nav-${slug(n.label)}`}>
                {n.label}
              </Link>
              {n.children && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {n.children.map((c) => (
                    <Link key={c.to} to={c.to} onClick={onClose} className="border border-white/15 px-3 py-1.5 font-display text-sm font-semibold uppercase tracking-wider text-white/70" data-testid={`mobile-nav-${slug(c.label)}`}>
                      {c.label}
                    </Link>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </nav>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="container-x relative mt-6 flex flex-col gap-3">
          <a href={`tel:${COMPANY.phoneIntl}`} className="flex items-center gap-2 text-white/70" data-testid="mobile-nav-phone">
            <Phone className="h-4 w-4 text-blue-500" /> {COMPANY.phoneDisplay}
          </a>
          <Link to="/contatti" onClick={onClose} className="btn-primary w-full" data-testid="mobile-nav-cta">
            Richiedi preventivo <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: EASE }}
        className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${scrolled || open ? "border-b border-white/10 bg-ink/85 backdrop-blur-xl" : "border-b border-transparent bg-transparent"}`}
        data-testid="navbar"
      >
        <div className="container-x flex h-24 items-center justify-between sm:h-32">
          <Link to="/" data-testid="nav-logo" className="relative z-50 flex items-center">
            <img src="/images/logo-desp-white.png" alt="Desp sas - Noleggi Piattaforme Aeree e Escavatori" className="h-[88px] w-auto sm:h-28" />
          </Link>

          <nav className="hidden items-center lg:flex" data-testid="desktop-nav">
            {NAV.map((n) =>
              n.children ? (
                <Dropdown key={n.to} item={n} />
              ) : (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`nav-link-${slug(n.label)}`}
                  className={({ isActive }) => `px-4 py-2 font-display text-[15px] font-semibold uppercase tracking-wider transition-colors ${isActive ? "text-white" : "text-white/70 hover:text-white"}`}
                >
                  {n.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="hidden items-center gap-5 lg:flex">
            <a href={`tel:${COMPANY.phoneIntl}`} className="flex items-center gap-2 font-mono text-sm text-white/70 transition-colors hover:text-white" data-testid="nav-phone-link">
              <Phone className="h-4 w-4 text-blue-500" /> {COMPANY.phoneDisplay}
            </a>
            <Link to="/contatti" className="btn-primary !py-2.5 !px-5 text-sm" data-testid="nav-cta-preventivo">
              Preventivo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Chiudi menu" : "Apri menu"}
            className="relative z-50 flex h-11 w-11 items-center justify-center border border-white/15 text-white lg:hidden"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.header>
      <MobileMenu open={open} onClose={() => setOpen(false)} />
    </>
  );
};
